import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Printer, Tag, PackagePlus, Trash2, Download } from "lucide-react";
import type { Deal } from "@/types/deal";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DealPriceTag } from "@/components/DealPriceTag";
import { printPriceTags } from "@/lib/printTags";
import { exportPriceTagsPdf } from "@/lib/printTags";

const DealSheet = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const deal = location.state?.deal as Deal | undefined;
  const [showOriginal, setShowOriginal] = useState(false);
  const [approved, setApproved] = useState(deal?.approved ?? false);
  const [showPriceTags, setShowPriceTags] = useState(false);
  const [addingToInventory, setAddingToInventory] = useState(false);
  const [addedToInventory, setAddedToInventory] = useState(false);
  const [sellingPrices, setSellingPrices] = useState<Record<string, string>>({});
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const deleteDeal = async () => {
    if (!deal) return;
    setDeleting(true);
    const { error: itemsErr } = await supabase.from("deal_items").delete().eq("deal_id", deal.id);
    if (itemsErr) {
      setDeleting(false);
      toast({ title: "Error", description: "Failed to delete deal items.", variant: "destructive" });
      return;
    }
    const { error: dealErr } = await supabase.from("deals").delete().eq("id", deal.id);
    setDeleting(false);
    if (dealErr) {
      toast({ title: "Error", description: "Failed to delete deal.", variant: "destructive" });
      return;
    }
    toast({ title: "Deleted", description: `Deal ${deal.batchCode} removed.` });
    navigate("/");
  };

  const addToInventory = async () => {
    if (!deal) return;

    const rows: any[] = [];
    const newPrices: Record<string, string> = { ...sellingPrices };
    for (const item of deal.items) {
      const suggested = sellingPrices[item.id] || item.originalPrice || item.gradePrice || "0";
      const orig = parseFloat(item.originalPrice) || 0;
      const gradeA = (orig * 0.55).toFixed(2);
      const gradeB = (orig * 0.35).toFixed(2);
      const gradeC = (orig * 0.20).toFixed(2);
      const input = window.prompt(
        `Set SELLING price for:\n\n${item.name}${item.platform ? ` (${item.platform})` : ""}\n\nOriginal: $${item.originalPrice}\nOffer paid: $${item.gradePrice}\n\nCurrent Grade Prices:\n  Grade A (55%): $${gradeA}\n  Grade B (35%): $${gradeB}\n  Grade C (20%): $${gradeC}\n\nLeave as-is, edit, or type SKIP to skip this item:`,
        suggested
      );
      if (input === null) {
        toast({ title: "Cancelled", description: "Inventory add cancelled." });
        return;
      }
      if (input.trim().toUpperCase() === "SKIP") continue;
      const price = parseFloat(input);
      if (isNaN(price) || price < 0) {
        toast({ title: "Invalid price", description: `Skipped ${item.name} (bad price).`, variant: "destructive" });
        continue;
      }
      newPrices[item.id] = price.toFixed(2);
      rows.push({
        name: item.name,
        price,
        platform: item.platform || null,
        category: "game",
        condition: "used",
        inventory_count: 1,
        image_url: item.photoUrl || item.imageUrl || null,
        description: `Grade ${item.grade} • ${item.priceType} • Batch ${deal.batchCode}`,
        active: true,
        featured: false,
      });
    }
    setSellingPrices(newPrices);

    if (rows.length === 0) {
      toast({ title: "Nothing added", description: "All items were skipped." });
      return;
    }

    setAddingToInventory(true);
    const { error } = await supabase.from("products").insert(rows);
    setAddingToInventory(false);
    if (error) {
      toast({ title: "Error", description: "Failed to add to inventory.", variant: "destructive" });
    } else {
      setAddedToInventory(true);
      toast({ title: "Added to Inventory!", description: `${rows.length} item(s) added to products.` });
    }
  };

  if (!deal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="font-mono text-muted-foreground">No deal data found.</p>
          <Button onClick={() => navigate("/")} variant="outline" className="font-mono">
            <ArrowLeft className="w-4 h-4 mr-2" /> GO BACK
          </Button>
        </div>
      </div>
    );
  }

  const total = deal.items.reduce((sum, item) => sum + parseFloat(item.gradePrice), 0);
  const originalTotal = deal.items.reduce((sum, item) => sum + (parseFloat(item.originalPrice) || 0), 0);

  const toggleApproved = async () => {
    const newVal = !approved;
    setApproved(newVal);
    const { error } = await supabase
      .from("deals")
      .update({ approved: newVal })
      .eq("id", deal.id);
    if (error) {
      setApproved(!newVal);
      toast({ title: "Error", description: "Failed to update approval.", variant: "destructive" });
    } else {
      toast({ title: newVal ? "Approved!" : "Unapproved", description: `Deal ${deal.batchCode} ${newVal ? "approved" : "unapproved"}.` });
    }
  };

  const handlePrintPriceTags = () => {
    if (!deal) return;
    printPriceTags(
      deal.items.map((item) => ({
        name: item.name,
        price: sellingPrices[item.id] || item.originalPrice || "0",
        upc: item.upc,
      })),
      "Price Tags"
    );
  };

  const handleDownloadBatch = () => {
    if (!deal) return;
    const tags = deal.items.map((item) => ({
      name: item.name,
      price: sellingPrices[item.id] || item.originalPrice || "0",
      upc: item.upc,
    }));
    exportPriceTagsPdf(tags, `price-tags-${deal.batchCode}.pdf`);
    toast({ title: "Downloaded", description: `${tags.length} tag(s) exported as PDF.` });
  };

  if (showPriceTags) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button onClick={() => setShowPriceTags(false)} variant="outline" size="sm" className="font-mono text-xs">
            <ArrowLeft className="w-3 h-3 mr-1" /> BACK TO DEAL
          </Button>
          <Button onClick={handlePrintPriceTags} size="sm" className="bg-primary text-primary-foreground font-mono text-xs ml-auto">
            <Printer className="w-3 h-3 mr-1" /> PRINT TAGS
          </Button>
        </div>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <p className="font-mono text-xs text-muted-foreground mb-3 text-center">
            Preview ({deal.items.length} tag{deal.items.length === 1 ? "" : "s"}) — uses identical format as POS inventory tags
          </p>
          <div className="space-y-2 flex flex-col items-center">
            {deal.items.map((item, i) => (
              <DealPriceTag
                key={i}
                item={item}
                price={sellingPrices[item.id] || item.originalPrice}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Screen-only controls */}
      <div className="print:hidden max-w-2xl mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
        <Button onClick={() => navigate("/")} variant="outline" size="sm" className="font-mono text-xs">
          <ArrowLeft className="w-3 h-3 mr-1" /> BACK
        </Button>
        <Button onClick={() => setShowOriginal(!showOriginal)} variant="outline" size="sm" className="font-mono text-xs">
          {showOriginal ? "HIDE ORIGINAL PRICES" : "SHOW ORIGINAL PRICES"}
        </Button>
        <Button onClick={() => window.print()} size="sm" className="bg-primary text-primary-foreground font-mono text-xs ml-auto">
          <Printer className="w-3 h-3 mr-1" /> PRINT
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" className="font-mono text-xs" disabled={deleting}>
              <Trash2 className="w-3 h-3 mr-1" /> {deleting ? "DELETING..." : "DELETE"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-mono">Delete this deal?</AlertDialogTitle>
              <AlertDialogDescription className="font-mono text-xs">
                This will permanently remove deal <span className="text-foreground">{deal.name}</span> ({deal.batchCode}) and all {deal.items.length} item(s). This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="font-mono">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteDeal} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 font-mono">
                Delete Deal
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Printable Content */}
      <div className="max-w-2xl mx-auto px-4 py-4 print:px-8 print:py-4">
        <h1 className="text-2xl font-bold font-mono text-foreground print:text-black mb-1">
          {deal.name}
        </h1>
        <div className="flex items-center gap-4 mb-6">
          <p className="text-xs font-mono text-muted-foreground print:text-gray-500">
            {deal.items.length} items • Batch: {deal.batchCode} • {new Date(deal.createdAt).toLocaleDateString()}
          </p>
        </div>

        {/* Approval Section */}
        <div className="print:hidden flex items-center gap-4 mb-6 p-3 bg-card rounded-lg border border-border">
          <div className="flex items-center gap-3 flex-1">
            <Checkbox
              checked={approved}
              onCheckedChange={toggleApproved}
              className="h-5 w-5 border-primary data-[state=checked]:bg-primary"
            />
            <span className="font-mono text-sm text-foreground">
              {approved ? "✅ DEAL APPROVED" : "MARK AS APPROVED"}
            </span>
          </div>
          {approved && (
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={addToInventory}
                disabled={addingToInventory || addedToInventory}
                size="sm"
                className="bg-primary text-primary-foreground font-mono text-xs"
              >
                <PackagePlus className="w-3 h-3 mr-1" />
                {addedToInventory ? "ADDED ✓" : addingToInventory ? "ADDING..." : "ADD TO INVENTORY"}
              </Button>
              <Button onClick={() => setShowPriceTags(true)} size="sm" className="bg-accent text-accent-foreground font-mono text-xs">
                <Tag className="w-3 h-3 mr-1" /> PRINT PRICE TAGS
              </Button>
              <Button onClick={handleDownloadBatch} size="sm" variant="outline" className="font-mono text-xs">
                <Download className="w-3 h-3 mr-1" /> EXPORT TAGS (.PDF)
              </Button>
            </div>
          )}
        </div>

        <table className="w-full border-collapse font-mono text-sm">
          <thead>
            <tr className="border-b-2 border-border print:border-black">
              <th className="text-left py-2 pr-2 w-16">PHOTO</th>
              <th className="text-left py-2 pr-2">ITEM</th>
              <th className="text-left py-2 pr-2">TYPE</th>
              <th className="text-left py-2 pr-2">GRADE</th>
              {showOriginal && <th className="text-right py-2 pr-2">ORIGINAL</th>}
              <th className="text-right py-2">OFFER</th>
            </tr>
          </thead>
          <tbody>
            {deal.items.map((item, i) => (
              <tr key={i} className="border-b border-border/50 print:border-gray-300">
                <td className="py-2 pr-2">
                  {(item.photoUrl || item.imageUrl) && (
                    <img src={item.photoUrl || item.imageUrl} alt={item.name} className="w-12 h-12 object-contain rounded" />
                  )}
                </td>
                <td className="py-2 pr-2">
                  <div className="font-semibold text-foreground print:text-black truncate max-w-[180px]">{item.name}</div>
                  {item.platform && <div className="text-[10px] text-muted-foreground print:text-gray-500">{item.platform}</div>}
                  {item.upc && <div className="text-[10px] text-muted-foreground print:text-gray-500">UPC: {item.upc}</div>}
                </td>
                <td className="py-2 pr-2 text-muted-foreground print:text-gray-600 text-xs">{item.priceType}</td>
                <td className="py-2 pr-2 text-muted-foreground print:text-gray-600 text-xs">{item.grade}</td>
                {showOriginal && (
                  <td className="py-2 pr-2 text-right text-muted-foreground print:text-gray-600">${item.originalPrice}</td>
                )}
                <td className="py-2 text-right font-bold text-primary print:text-black">${item.gradePrice}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {showOriginal && (
              <tr className="border-t-2 border-border print:border-black">
                <td colSpan={4} className="py-2 text-right font-bold text-foreground print:text-black">ORIGINAL TOTAL:</td>
                <td className="py-2 text-right font-bold text-muted-foreground print:text-gray-600">${originalTotal.toFixed(2)}</td>
                <td></td>
              </tr>
            )}
            <tr className={showOriginal ? "border-border" : "border-t-2 border-border print:border-black"}>
              <td colSpan={showOriginal ? 5 : 4} className="py-3 text-right font-bold text-foreground print:text-black">OFFER TOTAL:</td>
              <td className="py-3 text-right font-bold text-lg text-primary print:text-black">${total.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default DealSheet;
